import { Button } from '@lms/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@lms/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@lms/ui/dropdown-menu';
import { useToast } from '@lms/ui/use-toast';
import { EllipsisVerticalIcon, TrashIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslations } from 'use-intl';
import { removePortfolio } from '@/features/portfolios/portfolios-api';

// Ports app/[brand]/admin/portfolios/_components/overflowmenu.tsx together with
// the shared DeleteConfirmationMenu it used. The Next action redirected after
// deleting; here we surface failures with a toast and revalidate via SWR
// `mutate` (passed in as `onDeleted`).
export function OverflowMenu({
  id,
  onDeleted,
}: {
  id: number;
  onDeleted: () => void;
}) {
  const t = useTranslations('admin');
  const { toast } = useToast();
  const [warningOpen, setWarningOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await removePortfolio(id);
      setWarningOpen(false);
      onDeleted();
    } catch (e) {
      toast({ variant: 'destructive', description: `${e}` });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={warningOpen} onOpenChange={setWarningOpen}>
        <DialogTrigger />
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('are_you_sure')}</DialogTitle>
          </DialogHeader>
          <Button
            variant="destructive"
            className="w-full"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            <TrashIcon className="mr-2 h-4 w-4" />
            {t('remove', { what: '' }).trim()}
          </Button>
        </DialogContent>
      </Dialog>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger>
          <EllipsisVerticalIcon />
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            className="text-destructive"
            onClick={() => setWarningOpen(true)}
          >
            <TrashIcon className="mr-2 inline-block h-4 w-4" />
            {t('remove', { what: '' }).trim()}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
